import React from "react";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const clientReasons = [
    {
        number: "01",
        title: "We listen first",
        description:
            "Before writing code or recommending a solution, we take time to understand your business, your users and the problem you actually need to solve.",
    },
    {
        number: "02",
        title: "We keep things clear",
        description:
            "No unnecessary technical language or complicated processes. You always know what we are building, why it matters and what comes next.",
    },
    {
        number: "03",
        title: "We build for real use",
        description:
            "Our work is designed around the people who will actually use it — making the final product practical, reliable and easy to work with.",
    },
    {
        number: "04",
        title: "We stay involved",
        description:
            "A project doesn't end when it goes live. We remain available to improve, refine and support the technology as your business changes.",
    },
];

const ClientApproachSection = () => {
    return (
        <section className="w-full bg-[#fff] py-[90px] lg:py-[50px] font-sans">

            <div className="max-w-[1440px] w-full mx-auto px-[20px] lg:px-[80px]">

                {/* HEADER */}

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="
                        grid
                        grid-cols-1
                        lg:grid-cols-[0.9fr_1.1fr]
                        gap-[40px]
                        lg:gap-[120px]
                        mb-[70px]
                    "
                >

                    <div>
                        <span className="
                            font-graphik
                            text-[10px]
                            font-[600]
                            uppercase
                            tracking-[0.18em]
                            text-[#01386A]
                        ">
                            Our Approach
                        </span>

                        <h2  className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] lg:mb-[60px] mb-[30px] relative z-10 leading-[1.2]">
                            Good technology
                            <br />
                            starts with  understanding.
                           
                        </h2>
                    </div>


                    <div className="lg:pt-[45px]">

                        <p className="
                            max-w-[620px]
                            font-graphik
                            text-[#01386A]/60
                            text-[15px]
                            lg:text-[16px]
                            leading-[1.8]
                        ">
                            The best client relationships are built on more
                            than delivering a project. They come from
                            understanding the business, communicating clearly
                            and being there when things change.
                        </p>

                    </div>

                </motion.div>


                {/* CONTENT */}

                <div className="
                    border-t
                    border-[#01386A]/15
                ">

                    {clientReasons.map((item, index) => (

                        <motion.div
                            key={item.number}
                            initial={{ opacity: 0, y: 15 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                duration: 0.5,
                                delay: index * 0.08,
                            }}
                            className="
                                group
                                grid
                                grid-cols-[55px_1fr]
                                lg:grid-cols-[100px_0.7fr_1.3fr_40px]
                                gap-[20px]
                                lg:gap-[40px]
                                items-start
                                py-[30px]
                                lg:py-[38px]
                                border-b
                                border-[#01386A]/10
                                transition-all
                                duration-300
                                hover:bg-white
                                lg:px-[25px]
                            "
                        >

                            {/* NUMBER */}

                            <span className="
                                font-sora
                                text-[12px]
                                lg:text-[13px]
                                text-[#ED6800]
                                pt-[5px]
                            ">
                                {item.number}
                            </span>


                            {/* TITLE */}

                            <h3 className="
                                font-sora
                                text-[#01386A]
                                text-[20px]
                                lg:text-[25px]
                                font-[500]
                                leading-[1.2]
                            ">
                                {item.title}
                            </h3>


                            {/* DESCRIPTION */}

                            <p className="
                                col-start-2
                                lg:col-start-auto
                                max-w-[620px]
                                font-graphik
                                text-[#01386A]/55
                                text-[13px]
                                lg:text-[14px]
                                leading-[1.8]
                            ">
                                {item.description}
                            </p>


                            {/* ARROW */}

                            <div className="
                                hidden
                                lg:flex
                                justify-end
                                pt-[3px]
                            ">
                                <ArrowUpRight
                                    size={18}
                                    strokeWidth={1.4}
                                    className="
                                        text-[#01386A]/20
                                        transition-all
                                        duration-300
                                        group-hover:text-[#ED6800]
                                        group-hover:translate-x-[2px]
                                        group-hover:-translate-y-[2px]
                                    "
                                />
                            </div>

                        </motion.div>

                    ))}

                </div>


            </div>

        </section>
    );
};

export default ClientApproachSection;